/**
 * Acessor tipado de `localStorage` sob namespace fixo.
 *
 * Duas decisões carregam o arquivo:
 *
 * 1. **Namespace.** Num PWA o `localStorage` é compartilhado por origem. Sem
 *    prefixo, uma chave `theme` colide com qualquer outra coisa servida do
 *    mesmo domínio — e a colisão só aparece em produção.
 * 2. **Tolerância a falha em toda operação.** Modo privativo do iOS bloqueia a
 *    escrita, a cota estoura e o valor gravado por uma versão anterior do app
 *    pode não ser mais um JSON válido para `T`. Nenhum desses casos justifica
 *    derrubar a tela: preferência que não persiste é degradação aceitável,
 *    logo o fallback vence em silêncio.
 */

const NAMESPACE = 'arcus';

export interface TypedStorage<T> {
  read(): T;
  write(value: T): void;
  clear(): void;
}

/** Cria um acessor de `localStorage` para uma chave, com valor de fallback. */
export function createStorage<T>(key: string, fallback: T): TypedStorage<T> {
  const namespacedKey = `${NAMESPACE}:${key}`;

  return {
    read(): T {
      try {
        const raw = window.localStorage.getItem(namespacedKey);
        return raw === null ? fallback : (JSON.parse(raw) as T);
      } catch {
        // storage indisponível ou JSON corrompido — segue com o fallback
        return fallback;
      }
    },

    write(value: T): void {
      try {
        window.localStorage.setItem(namespacedKey, JSON.stringify(value));
      } catch {
        // cota estourada ou modo privativo — a preferência não persiste, o app segue
      }
    },

    clear(): void {
      try {
        window.localStorage.removeItem(namespacedKey);
      } catch {
        // storage indisponível — não há estado a limpar
      }
    },
  };
}
